import { useCallback, useMemo } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";

import { UserPolices } from "constant";
import features from "features";
import { formatDate } from "helpers/formatDate";
import usePolicy from "hooks/usePolicy";
import { ProjectInterface } from "scenes/MainPage/ducks";

import IconButton from "components/buttons/IconButton/IconButton";
import FormField from "components/inputs/FormField/FormField";

import PencilIcon from "assets/icons/pencil.svg";
import Trash2Icon from "assets/icons/trash-2.svg";

import { LocalStorageProvider } from "services/LocalStorageProvider";
import styles from "./project-preview.module.scss";

interface ProjectPreviewInterface extends ProjectInterface {
  updateProjectsList?: () => void;
  currentPage?: number;
  isLastProjectOnPage?: boolean;
  onPrevPage?: () => void;
}

const ProjectPreview = ({
  name,
  created_at,
  id,
  currentPage,
  isLastProjectOnPage,
  onPrevPage,
  updateProjectsList
}: ProjectPreviewInterface) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { hasPolicy } = usePolicy();

  const onDelete = useCallback(() => {
    dispatch(
      features.modal.actions.showModal({
        modalType: "DELETE",
        modalProps: {
          title: "Delete project?",
          subtitle: `Are you sure you want to delete project "${name}"? This action cannot be undone.`,
          onAcceptClick: () =>
            dispatch(
              features.main.actions.deleteProjectRequest({
                params: {
                  id
                },
                onSuccess: () => {
                  if (isLastProjectOnPage) {
                    onPrevPage();
                  }
                  updateProjectsList();
                  dispatch(features.modal.actions.hideModal());
                }
              })
            )
        }
      })
    );
  }, [dispatch, id, name, updateProjectsList, isLastProjectOnPage, onPrevPage]);

  const handleFormSubmit = useCallback(
    (values) => {
      dispatch(
        features.main.actions.renameProjectRequest({
          params: {
            id
          },
          fields: values,
          onSuccess: () => {
            dispatch(
              features.modal.actions.showModal({
                modalType: "SUCCESS",
                modalProps: {
                  title: "Rename project",
                  acceptButtonAction: () => {
                    dispatch(features.modal.actions.hideModal());
                  }
                }
              })
            );
          }
        })
      );
    },
    [dispatch, id]
  );

  const onRename = useCallback(() => {
    dispatch(
      features.modal.actions.showModal({
        modalType: "FORM",
        modalProps: {
          title: "Rename project",
          description: "Please enter new name.",
          initialValues: { name: "" },
          onSubmit: handleFormSubmit,
          validationSchema: Yup.object().shape({
            name: Yup.string().required("Project name is required")
          }),
          acceptButtonLabel: "Save",
          formContent: (
            <>
              <FormField name="name" placeholder="New name" />
            </>
          )
        }
      })
    );
  }, [dispatch, handleFormSubmit]);

  const operateLink = useCallback(() => {
    const page = currentPage.toString();
    LocalStorageProvider.routes.setProjectsCurrentPage(page);
    return `/map/${id}?page=${page}`;
  }, [currentPage, id]);

  const onProjectClick = useCallback(() => {
    if (hasPolicy(UserPolices.PROJECT_READ)) navigate(operateLink());
  }, [hasPolicy, navigate, operateLink]);

  const optionsList = useMemo(() => {
    const options = [
      ...(hasPolicy(UserPolices.PROJECT_UPDATE)
        ? [{ title: "Edit", icon: PencilIcon, action: onRename }]
        : []),
      ...(hasPolicy(UserPolices.PROJECT_DELETE)
        ? [{ title: "Delete", icon: Trash2Icon, action: onDelete }]
        : [])
    ];
    return options;
  }, [hasPolicy, onDelete, onRename]);

  return (
    <div onClick={onProjectClick} className={styles["project"]}>
      <div className={styles["project__title"]}>{name}</div>
      <div className={styles["project__date"]}>{formatDate(created_at)}</div>
      <div className={styles["project__actions-container"]}>
        {optionsList.length > 0 &&
          optionsList.map((option, index) => (
            <IconButton
              icon={option.icon}
              className={styles["project__actions-button"]}
              onClick={(e) => {
                e.stopPropagation();
                option.action();
              }}
              key={`${option.title}:${index}`}
              alt={`${option.title}-icon`}
            />
          ))}
      </div>
    </div>
  );
};
export default ProjectPreview;
